import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, FilterIcon, DownloadIcon, MoreHorizontalIcon, ChevronDownIcon, UserIcon, HomeIcon } from 'lucide-react';
export function RenewalsTable() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Mock data for renewals
  const mockRenewals = [{
    id: '1',
    client: 'John Doe',
    property: '123 Main St, Anytown',
    lender: 'TD Bank',
    rate: '3.25%',
    term: '5 years',
    maturityDate: '2023-10-15',
    payment: '$1,450/month',
    equity: '$120,000',
    status: 'Upcoming',
    broker: 'John Broker'
  }, {
    id: '2',
    client: 'Jane Smith',
    property: '456 Oak Ave, Somewhere',
    lender: 'RBC',
    rate: '2.89%',
    term: '3 years',
    maturityDate: '2023-11-01',
    payment: '$1,350/month',
    equity: '$95,000',
    status: 'Upcoming',
    broker: 'Jane Agent'
  }, {
    id: '3',
    client: 'Michael Johnson',
    property: '789 Pine Rd, Elsewhere',
    lender: 'Scotiabank',
    rate: '3.45%',
    term: '5 years',
    maturityDate: '2023-12-10',
    payment: '$1,750/month',
    equity: '$150,000',
    status: 'Upcoming',
    broker: 'John Broker'
  }, {
    id: '4',
    client: 'Emily Williams',
    property: '321 Maple Dr, Nowhere',
    lender: 'CIBC',
    rate: '3.10%',
    term: '4 years',
    maturityDate: '2024-01-15',
    payment: '$1,550/month',
    equity: '$130,000',
    status: 'Upcoming',
    broker: 'Jane Agent'
  }, {
    id: '5',
    client: 'David Brown',
    property: '654 Elm St, Anywhere',
    lender: 'First National',
    rate: '3.35%',
    term: '5 years',
    maturityDate: '2024-02-01',
    payment: '$1,650/month',
    equity: '$140,000',
    status: 'Upcoming',
    broker: 'John Broker'
  }];
  // Filter renewals based on search term
  const filteredRenewals = mockRenewals.filter(renewal => renewal.client.toLowerCase().includes(searchTerm.toLowerCase()) || renewal.property.toLowerCase().includes(searchTerm.toLowerCase()) || renewal.lender.toLowerCase().includes(searchTerm.toLowerCase()));
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Upcoming':
        return 'badge badge-warning';
      case 'In Progress':
        return 'badge badge-primary';
      case 'Completed':
        return 'badge badge-success';
      case 'Lost':
        return 'badge badge-danger';
      default:
        return 'badge badge-neutral';
    }
  };
  return <div data-id="element-2195">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center mb-6" data-id="element-2196">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-2197">Renewals</h1>
      </div>
      <div className="card" data-id="element-2198">
        {/* Table Toolbar */}
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-2199">
          <div className="relative w-full sm:max-w-xs" data-id="element-2200">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-2201">
              <SearchIcon className="h-5 w-5 text-neutral-400" data-id="element-2202" />
            </div>
            <input type="text" className="input w-full pl-10" placeholder="Search renewals..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2203" />
          </div>
          <div className="flex items-center space-x-2" data-id="element-2204">
            <button className="btn btn-outline relative" onClick={() => setIsFilterOpen(!isFilterOpen)} data-id="element-2205">
              <FilterIcon className="mr-2 h-4 w-4" data-id="element-2206" />
              Filters
            </button>
            <button className="btn btn-outline" data-id="element-2207">
              <DownloadIcon className="mr-2 h-4 w-4" data-id="element-2208" />
              Export
            </button>
          </div>
        </div>
        {/* Filters Panel */}
        {isFilterOpen && <div className="mb-4 rounded-md border border-neutral-200 bg-neutral-50 p-4" data-id="element-2209">
            <div className="flex items-center justify-between" data-id="element-2210">
              <h3 className="text-sm font-medium text-neutral-900" data-id="element-2211">Filters</h3>
              <button className="text-sm text-primary-600 hover:text-primary-500" data-id="element-2212">
                Clear all
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4" data-id="element-2213">
              {/* Lender Filter */}
              <div data-id="element-2214">
                <h4 className="mb-2 text-xs font-medium text-neutral-500" data-id="element-2215">
                  Lender
                </h4>
                <div className="space-y-2" data-id="element-2216">
                  {['TD Bank', 'RBC', 'Scotiabank', 'CIBC', 'First National'].map(lender => <label key={lender} className="flex items-center" data-id="element-2217">
                      <input type="checkbox" className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" data-id="element-2218" />
                      <span className="ml-2 text-sm text-neutral-700" data-id="element-2219">
                        {lender}
                      </span>
                    </label>)}
                </div>
              </div>
              {/* Status Filter */}
              <div data-id="element-2220">
                <h4 className="mb-2 text-xs font-medium text-neutral-500" data-id="element-2221">
                  Status
                </h4>
                <div className="space-y-2" data-id="element-2222">
                  {['Upcoming', 'In Progress', 'Completed', 'Lost'].map(status => <label key={status} className="flex items-center" data-id="element-2223">
                        <input type="checkbox" className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" data-id="element-2224" />
                        <span className="ml-2 text-sm text-neutral-700" data-id="element-2225">
                          {status}
                        </span>
                      </label>)}
                </div>
              </div>
              {/* Maturity Date Filter */}
              <div data-id="element-2226">
                <h4 className="mb-2 text-xs font-medium text-neutral-500" data-id="element-2227">
                  Maturity Date
                </h4>
                <div className="space-y-2" data-id="element-2228">
                  {[{
                value: 'all',
                label: 'All Time'
              }, {
                value: '30-days',
                label: 'Next 30 Days'
              }, {
                value: '60-days',
                label: 'Next 60 Days'
              }, {
                value: '90-days',
                label: 'Next 90 Days'
              }].map(option => <label key={option.value} className="flex items-center" data-id="element-2229">
                      <input type="radio" name="maturity-date" className="h-4 w-4 border-neutral-300 text-primary-600 focus:ring-primary-500" data-id="element-2230" />
                      <span className="ml-2 text-sm text-neutral-700" data-id="element-2231">
                        {option.label}
                      </span>
                    </label>)}
                </div>
              </div>
              {/* Broker Filter */}
              <div data-id="element-2232">
                <h4 className="mb-2 text-xs font-medium text-neutral-500" data-id="element-2233">
                  Assigned Broker
                </h4>
                <div className="space-y-2" data-id="element-2234">
                  {['John Broker', 'Jane Agent'].map(broker => <label key={broker} className="flex items-center" data-id="element-2235">
                      <input type="checkbox" className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" data-id="element-2236" />
                      <span className="ml-2 text-sm text-neutral-700" data-id="element-2237">
                        {broker}
                      </span>
                    </label>)}
                </div>
              </div>
            </div>
          </div>}
        {/* Renewals Table */}
        <div className="overflow-x-auto" data-id="element-2238">
          <table className="min-w-full divide-y divide-neutral-200" data-id="element-2239">
            <thead className="bg-neutral-50" data-id="element-2240">
              <tr data-id="element-2241">
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-2242">
                  <div className="flex items-center" data-id="element-2243">
                    Property
                    <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-2244" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-2245">
                  <div className="flex items-center" data-id="element-2246">
                    Client
                    <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-2247" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-2248">
                  Lender
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-2249">
                  <div className="flex items-center" data-id="element-2250">Rate / Term</div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-2251">
                  <div className="flex items-center" data-id="element-2252">
                    Maturity Date
                    <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-2253" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-2254">
                  Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-2255">
                  Equity Est.
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-2256">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-2257">
                  Assigned To
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-2258">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200" data-id="element-2259">
              {filteredRenewals.map(renewal => <tr key={renewal.id} className="hover:bg-neutral-50" data-id="element-2260">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2261">
                    <div className="flex items-center" data-id="element-2262">
                      <HomeIcon className="h-4 w-4 text-neutral-400 mr-2" data-id="element-2263" />
                      <Link to={`/renewals/${renewal.id}`} className="text-sm font-medium text-primary-600 hover:text-primary-500" data-id="element-2264">
                        {renewal.property}
                      </Link>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2265">
                    <div className="flex items-center" data-id="element-2266">
                      <div className="h-8 w-8 rounded-full bg-neutral-200 flex items-center justify-center" data-id="element-2267">
                        <UserIcon className="h-4 w-4 text-neutral-600" data-id="element-2268" />
                      </div>
                      <span className="ml-2 text-sm text-neutral-900" data-id="element-2269">
                        {renewal.client}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500" data-id="element-2270">
                    {renewal.lender}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500" data-id="element-2271">
                    {renewal.rate} / {renewal.term}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500" data-id="element-2272">
                    {new Date(renewal.maturityDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500" data-id="element-2273">
                    {renewal.payment}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500" data-id="element-2274">
                    {renewal.equity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2275">
                    <span className={getStatusBadgeClass(renewal.status)} data-id="element-2276">
                      {renewal.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500" data-id="element-2277">
                    {renewal.broker}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2278">
                    <button className="text-primary-600 hover:text-primary-900" data-id="element-2279">
                      <MoreHorizontalIcon className="h-5 w-5" data-id="element-2280" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
          {filteredRenewals.length === 0 && <div className="py-12 text-center" data-id="element-2281">
              <p className="text-neutral-500" data-id="element-2282">
                No renewals found matching your criteria.
              </p>
            </div>}
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-neutral-200 bg-white px-4 py-3 sm:px-6" data-id="element-2283">
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between" data-id="element-2284">
            <div data-id="element-2285">
              <p className="text-sm text-neutral-700" data-id="element-2286">
                Showing <span className="font-medium" data-id="element-2287">1</span> to{' '}
                <span className="font-medium" data-id="element-2288">{filteredRenewals.length}</span>{' '}
                of{' '}
                <span className="font-medium" data-id="element-2289">{filteredRenewals.length}</span>{' '}
                results
              </p>
            </div>
            <div data-id="element-2290">
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination" data-id="element-2291">
                <a href="#" className="relative inline-flex items-center rounded-l-md px-2 py-2 text-neutral-400 ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50" data-id="element-2292">
                  <span className="sr-only" data-id="element-2293">Previous</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-2294">
                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" data-id="element-2295" />
                  </svg>
                </a>
                <a href="#" aria-current="page" className="relative z-10 inline-flex items-center bg-primary-600 px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600" data-id="element-2296">
                  1
                </a>
                <a href="#" className="relative inline-flex items-center rounded-r-md px-2 py-2 text-neutral-400 ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50" data-id="element-2297">
                  <span className="sr-only" data-id="element-2298">Next</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-2299">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" data-id="element-2300" />
                  </svg>
                </a>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>;
}